import {
  useDispatchSimulationAllStoresEmail,
  useReapproSimulationAllStoresEmail,
} from '@gozoki/api'
import { AxiosError } from 'axios'
import { useState } from 'react'
import { toast } from 'sonner'
import { Button } from '~/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/components/ui/dialog'

interface ReapproSimulationEmailDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

const parseEmails = (raw: string): string[] =>
  raw
    .split(/[,;\r\n]+/)
    .map((e) => e.trim().toLowerCase())
    .filter((e) => e.length > 0)

const extractErrorMessage = (error: unknown, fallback: string) => {
  if (
    error instanceof AxiosError &&
    error.response?.data &&
    typeof error.response.data === 'object' &&
    'message' in error.response.data
  ) {
    return error.response.data.message as string
  }
  return fallback
}

export const ReapproSimulationEmailDialog = ({
  open,
  onOpenChange,
}: ReapproSimulationEmailDialogProps) => {
  const [emailsRaw, setEmailsRaw] = useState('')
  const [sendToPilotage, setSendToPilotage] = useState(false)
  const [runReappro, setRunReappro] = useState(true)
  const [runDispatch, setRunDispatch] = useState(false)

  const { mutateAsync: requestReapproSimulationAsync, isPending: isRequestingReappro } =
    useReapproSimulationAllStoresEmail()
  const { mutateAsync: requestDispatchSimulationAsync, isPending: isRequestingDispatch } =
    useDispatchSimulationAllStoresEmail()
  const isPending = isRequestingReappro || isRequestingDispatch

  const resetState = () => {
    setEmailsRaw('')
    setSendToPilotage(false)
    setRunReappro(true)
    setRunDispatch(false)
  }

  const handleOpenChange = (nextOpen: boolean) => {
    onOpenChange(nextOpen)
    if (!nextOpen) resetState()
  }

  const handleSubmit = async () => {
    const emails = parseEmails(emailsRaw)

    if (emails.length === 0 && !sendToPilotage) {
      toast.error('Veuillez renseigner au moins un email ou cocher le pilotage Lucie.')
      return
    }
    if (!runReappro && !runDispatch) {
      toast.error('Veuillez sélectionner au moins une simulation (Réappro ou Dispatch).')
      return
    }

    const payload = { emails, sendToPilotage }
    const tasks: Promise<{ label: string; ok: boolean; message?: string }>[] = []

    if (runReappro) {
      tasks.push(
        requestReapproSimulationAsync(payload)
          .then(() => ({ label: 'Réappro', ok: true }))
          .catch((error: unknown) => ({
            label: 'Réappro',
            ok: false,
            message: extractErrorMessage(
              error,
              'Impossible de lancer la simulation réappro dans toutes les boutiques.',
            ),
          })),
      )
    }

    if (runDispatch) {
      tasks.push(
        requestDispatchSimulationAsync(payload)
          .then(() => ({ label: 'Dispatch', ok: true }))
          .catch((error: unknown) => ({
            label: 'Dispatch',
            ok: false,
            message: extractErrorMessage(
              error,
              'Impossible de lancer la simulation dispatch dans toutes les boutiques.',
            ),
          })),
      )
    }

    const results = await Promise.all(tasks)
    const successes = results.filter((r) => r.ok).map((r) => r.label)
    const failures = results.filter((r) => !r.ok)

    for (const f of failures) {
      toast.error(`${f.label} : ${f.message}`)
    }

    if (successes.length > 0) {
      const plural = successes.length > 1
      toast.success(
        `Simulation${plural ? 's' : ''} ${successes.join(' + ')} en cours de génération.`,
        {
          description: `Vous recevrez un email avec ${plural ? 'les fichiers' : 'le fichier'} dans quelques minutes.`,
        },
      )
    }

    if (failures.length === 0) {
      onOpenChange(false)
      resetState()
    }
  }

  const submitDisabled = isPending || (!runReappro && !runDispatch)

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Simulation Excel toutes boutiques</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <p className="text-sm font-medium mb-2">Simulations à lancer</p>
            <div className="flex flex-row gap-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={runReappro}
                  onChange={(e) => setRunReappro(e.target.checked)}
                  disabled={isPending}
                  className="w-4 h-4 rounded accent-primary"
                />
                <span className="text-sm">Réappro</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={runDispatch}
                  onChange={(e) => setRunDispatch(e.target.checked)}
                  disabled={isPending}
                  className="w-4 h-4 rounded accent-primary"
                />
                <span className="text-sm">Dispatch</span>
              </label>
            </div>
          </div>

          <p className="text-sm text-muted-foreground">
            Saisissez les emails destinataires, séparés par des virgules, points-virgules ou retours
            à la ligne.
          </p>

          <textarea
            className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            rows={3}
            placeholder="email1@exemple.com, email2@exemple.com"
            value={emailsRaw}
            onChange={(event) => setEmailsRaw(event.target.value)}
            disabled={isPending}
          />
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={sendToPilotage}
              onChange={(e) => setSendToPilotage(e.target.checked)}
              disabled={isPending}
              className="w-4 h-4 rounded accent-primary"
            />
            <span className="text-sm">Envoyer à tout le pilotage Lucie</span>
          </label>
        </div>
        <DialogFooter>
          <DialogClose render={<Button variant="outline" />}>Annuler</DialogClose>
          <Button onClick={handleSubmit} disabled={submitDisabled}>
            Envoyer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
